import dayjs from 'dayjs';

/**
 * Períodos prontos do seletor. A semana começa na segunda (igual ao
 * agrupamento semanal do backend). "Esta semana", "Este mês" e "Este ano"
 * vão até o fim da semana, do mês e do ano, para os agendamentos que ainda
 * vêm entrarem em "Ainda por vir" e "Previsto"; os "Últimos N meses" vão até o
 * fim do mês atual pelo mesmo motivo (senão o mês corrente só enxerga o que
 * ainda vem hoje e a previsão do mês fica errada).
 * "Próximo mês" e "Próximos 30 dias" olham só para a frente: é por eles que se
 * vê o que já está marcado depois do mês atual (o backend aceita datas futuras).
 * Na comparação, o backend (StatsPeriod) compara o que só existe até hoje
 * (realizados, recebido) com os mesmos dias já vividos do período anterior.
 *
 * O período é { key, start, end, compare }: `compare` escolhe com o que os
 * números e os gráficos são comparados (ver COMPARE_MODES); 'none' (padrão)
 * mostra só o período, sem comparação.
 */

const FORMAT = 'YYYY-MM-DD';

function mondayOf(date) {
  const weekday = (date.day() + 6) % 7; // segunda = 0
  return date.subtract(weekday, 'day').startOf('day');
}

export const PERIOD_PRESETS = [
  {
    key: 'this-week',
    label: 'Esta semana',
    range: (today) => [mondayOf(today), mondayOf(today).add(6, 'day')],
  },
  {
    key: 'last-week',
    label: 'Semana passada',
    range: (today) => {
      const start = mondayOf(today).subtract(7, 'day');
      return [start, start.add(6, 'day')];
    },
  },
  {
    key: 'this-month',
    label: 'Este mês',
    range: (today) => [today.startOf('month'), today.endOf('month')],
  },
  {
    key: 'last-month',
    label: 'Mês passado',
    range: (today) => {
      const start = today.subtract(1, 'month').startOf('month');
      return [start, start.endOf('month')];
    },
  },
  {
    key: 'next-month',
    label: 'Próximo mês',
    range: (today) => {
      const start = today.add(1, 'month').startOf('month');
      return [start, start.endOf('month')];
    },
  },
  {
    key: 'next-30-days',
    label: 'Próximos 30 dias',
    range: (today) => [today.startOf('day'), today.add(29, 'day')],
  },
  {
    key: 'last-3-months',
    label: 'Últimos 3 meses',
    range: (today) => [today.subtract(2, 'month').startOf('month'), today.endOf('month')],
  },
  {
    key: 'last-12-months',
    label: 'Últimos 12 meses',
    range: (today) => [today.subtract(11, 'month').startOf('month'), today.endOf('month')],
  },
  {
    key: 'this-year',
    label: 'Este ano',
    range: (today) => [today.startOf('year'), today.endOf('year')],
  },
  {
    key: 'last-year',
    label: 'Ano passado',
    range: (today) => {
      const start = today.subtract(1, 'year').startOf('year');
      return [start, start.endOf('year')];
    },
  },
];

/** Com o que comparar: o período logo antes ou o mesmo período um ano antes. */
export const COMPARE_MODES = [
  { key: 'previous', label: 'Período anterior' },
  { key: 'year', label: 'Mesmo período do ano passado' },
];

export const NO_COMPARE = 'none';

export const DEFAULT_COMPARE = NO_COMPARE;

export const DEFAULT_PRESET = 'last-12-months';

/** Período { key, start, end } de um atalho. */
export function presetPeriod(key, compare = DEFAULT_COMPARE) {
  const preset = PERIOD_PRESETS.find((item) => item.key === key) || PERIOD_PRESETS[2];
  const [start, end] = preset.range(dayjs());
  return { key: preset.key, start: start.format(FORMAT), end: end.format(FORMAT), compare };
}

export function customPeriod(start, end, compare = DEFAULT_COMPARE) {
  return { key: 'custom', start: dayjs(start).format(FORMAT), end: dayjs(end).format(FORMAT), compare };
}

/**
 * Período do guia dos primeiros passos: do dia 1 do mês até 30 dias à frente,
 * para o atendimento recém-marcado (quase sempre num dia que ainda vem) entrar
 * no Resumo. Fora do guia a tela abre no DEFAULT_PRESET.
 */
export function guidePeriod() {
  const today = dayjs();
  return customPeriod(today.startOf('month'), today.add(30, 'day'));
}

/**
 * Textos da comparação do período (`enabled` false quando não compara):
 *  - short: legenda do gráfico ("2025", "Ano anterior", "Período anterior");
 *  - suffix: complemento de uma série ("Recebido em 2025");
 *  - before: rótulo do valor comparado nos indicadores ("em 2025: 12");
 *  - long: frase do cabeçalho e do PDF.
 */
export function compareLabels(period) {
  if (!period.compare || period.compare === NO_COMPARE) {
    return { enabled: false, short: '', suffix: '', before: '', long: '' };
  }
  if (period.compare === 'year') {
    const from = dayjs(period.start).year() - 1;
    const to = dayjs(period.end).year() - 1;
    if (from === to) {
      return { enabled: true, short: String(from), suffix: `em ${from}`, before: `em ${from}`, long: `comparado com o mesmo período de ${from}` };
    }
    return { enabled: true, short: 'Ano anterior', suffix: 'um ano antes', before: 'um ano antes', long: 'comparado com o mesmo período do ano anterior' };
  }
  return { enabled: true, short: 'Período anterior', suffix: 'no período anterior', before: 'antes', long: 'comparado com o período anterior de mesmo tamanho' };
}

/** "1 a 11 de outubro de 2026", "28/09/2026 a 04/10/2026". */
export function describePeriod(start, end) {
  const from = dayjs(start);
  const to = dayjs(end);
  const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
  if (from.isSame(to, 'month')) {
    if (from.date() === to.date()) return `${from.date()} de ${months[from.month()]} de ${from.year()}`;
    return `${from.date()} a ${to.date()} de ${months[from.month()]} de ${from.year()}`;
  }
  return `${from.format('DD/MM/YYYY')} a ${to.format('DD/MM/YYYY')}`;
}

/** Limite do backend (StatsPeriod::MAX_DAYS). */
export const MAX_PERIOD_DAYS = 731;
